import React, { useState } from 'react';
import { Layers, Download, Map as MapIcon } from 'lucide-react';
import { useJob } from '../hooks/useJob';
import { VT_GEOJSON_MAX_FEATURES, tileKinds, tilesPending, vtApi } from '../lib/vector-tiles';

const LABELS = { pseudo_point: 'Pseudosection', electrode: 'Electrodes', xyz_point: 'Points' };

/** What the card shows for a dataset's metadata (pure; unit-tested). */
export function vectorTilesCardState(metadata, threshold = VT_GEOJSON_MAX_FEATURES) {
  const count = Number(metadata?.feature_count) || 0;
  const tiles = metadata?.tiles || {};
  if (count <= threshold && !tiles.ready) return { show: false };
  return {
    show: true,
    count,
    ready: !!tiles.ready,
    pending: tilesPending(metadata, threshold),
    reason: tiles.reason || tiles.stale || null,
    queuedJobId: !tiles.ready ? tiles.job?.id || null : null,
    kinds: tileKinds(metadata),
    exports: tiles.pmtiles || {},
  };
}

const fmtBytes = (n) => (n > 1e6 ? `${(n / 1e6).toFixed(1)} MB` : `${Math.max(1, Math.round((n || 0) / 1e3))} KB`);

/**
 * Dataset page card for large datasets (docs/VECTOR_TILES.md): build the vector tiles,
 * export a kind as a PMTiles archive (a `pmtiles_export` job), download it or open it on
 * the map. Hidden for datasets small enough for GeoJSON.
 */
export default function VectorTilesCard({ datasetId, metadata, onChanged, onOpenMap, showToast = () => {} }) {
  const state = vectorTilesCardState(metadata);
  const [jobId, setJobId] = useState(null);
  const [jobKind, setJobKind] = useState(null); // 'build' | <kind> being exported
  const [busy, setBusy] = useState(false);
  const watchId = jobId || state.queuedJobId || null;
  const { job } = useJob(watchId, {
    onSettled: (j) => {
      if (jobId) { // started here; a job recorded on the dataset earlier settles quietly
        if (j.status === 'failed') showToast(j.error || 'The tiles job failed', 'error');
        if (j.status === 'succeeded') showToast(jobKind && jobKind !== 'build' ? 'PMTiles archive ready' : 'Vector tiles ready', 'success');
        setJobId(null);
        setJobKind(null);
      }
      if (onChanged && j.status === 'succeeded') onChanged();
    },
  });
  if (!state.show) return null;
  const running = !!watchId && (!job || job.status === 'queued' || job.status === 'running');

  const start = async (kind, call) => {
    setBusy(true);
    try {
      const res = await call();
      setJobKind(kind);
      setJobId(res.data?.job_id || res.data?.job?.id || null);
    } catch (e) {
      showToast(e?.response?.data?.detail || 'Could not start the job', 'error');
    } finally {
      setBusy(false);
    }
  };

  const download = async (kind) => {
    try {
      const res = await vtApi.pmtilesUrl(datasetId, kind);
      window.open(res.data.url, '_blank', 'noopener');
    } catch (e) {
      showToast(e?.response?.data?.detail || 'Could not get the archive link', 'error');
    }
  };

  const btn = 'px-3 py-1.5 text-xs rounded-md border border-border hover:bg-muted disabled:opacity-50 inline-flex items-center gap-1.5';
  return (
    <div className="bg-card border border-border rounded-lg p-6 shadow-sm" data-testid="vector-tiles-card">
      <h2 className="text-lg font-semibold mb-2 flex items-center gap-2"><Layers className="w-5 h-5" />Vector tiles</h2>
      <p className="text-sm text-muted-foreground mb-3">
        {state.count.toLocaleString()} features: above {VT_GEOJSON_MAX_FEATURES.toLocaleString()}, the map draws this
        dataset from vector tiles instead of loading it all at once.
      </p>
      {running && (
        <p className="text-sm mb-3" data-testid="vector-tiles-running">
          {job?.message || 'Queued'}{job?.status === 'running' && job.progress > 0 && ` (${Math.round(job.progress * 100)}%)`}
        </p>
      )}
      {!state.ready && !running && (
        <div className="space-y-2">
          {state.reason && <p className="text-xs text-muted-foreground">Tiles not ready: {state.reason}</p>}
          <button className={btn} disabled={busy} onClick={() => start('build', () => vtApi.build(datasetId))}
            data-testid="vector-tiles-build">
            <Layers className="w-3.5 h-3.5" />Build vector tiles
          </button>
        </div>
      )}
      {state.ready && (
        <div className="space-y-2">
          {state.kinds.map(kind => {
            const exp = state.exports[kind];
            return (
              <div key={kind} className="flex flex-wrap items-center gap-2 text-sm">
                <span className="w-28">{LABELS[kind] || kind}</span>
                <button className={btn} disabled={busy || running}
                  onClick={() => start(kind, () => vtApi.exportPmtiles(datasetId, kind))}
                  data-testid={`pmtiles-export-${kind}`}>
                  {exp ? 'Re-export PMTiles' : 'Export PMTiles'}
                </button>
                {exp && (
                  <>
                    <button className={btn} onClick={() => download(kind)} data-testid={`pmtiles-download-${kind}`}>
                      <Download className="w-3.5 h-3.5" />{fmtBytes(exp.bytes)}
                    </button>
                    {onOpenMap && (
                      <button className={btn} onClick={() => onOpenMap('pmtiles')}>
                        <MapIcon className="w-3.5 h-3.5" />Open archive on map
                      </button>
                    )}
                  </>
                )}
              </div>
            );
          })}
          <p className="text-xs text-muted-foreground">
            The map uses live tiles (always current). A PMTiles archive is a snapshot for sharing or offline use;
            its download link expires after an hour.
          </p>
        </div>
      )}
    </div>
  );
}
